/**
 * Shared client-side fetch helpers for API calls.
 * Eliminates the repeated fetch + JSON parse + error throw pattern across hooks.
 */

import { handleAuthError } from "@/lib/auth-redirect";

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public detail?: string,
    public body?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

type FetchJsonOpts = Omit<RequestInit, "body"> & {
  body?: unknown;
};

/**
 * Typed fetch wrapper. Throws `ApiError` on non-2xx responses.
 *
 * ```ts
 * const { tags } = await apiJson<{ tags: Tag[] }>("/api/tags");
 * const { lookup } = await apiJson<{ lookup: Row }>("/api/lookups", {
 *   method: "POST",
 *   body: { domain: "color", code: "red", label: "Red" },
 * });
 * ```
 */
export async function apiJson<T = Record<string, unknown>>(
  url: string,
  opts?: FetchJsonOpts,
): Promise<T> {
  const { body, headers, ...rest } = opts ?? {};

  const init: RequestInit = {
    ...rest,
    cache: "no-store" as RequestCache,
    headers: {
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...headers,
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  };

  let res = await fetch(url, init);
  // Fresh / evicted Turbopack compiles can 404 the first POST to login.
  if (res.status === 404 && /\/api\/auth\/login(\?|$)/.test(url)) {
    await new Promise((resolve) => setTimeout(resolve, 400));
    res = await fetch(url, init);
  }
  const json = await res.json().catch(() => ({} as Record<string, unknown>));

  if (!res.ok) {
    const msg =
      typeof json.error === "string" ? json.error : `Request failed (${res.status})`;
    const detail = typeof json.detail === "string" ? json.detail : undefined;
    const error = new ApiError(msg, res.status, detail, json as Record<string, unknown>);
    // Failed login/password calls are 401s but must stay on the form — not a session bounce.
    const isCredentialPost = /\/api\/auth\/(login|forgot-password|reset-password|change-password)(\?|$)/.test(url);
    if (res.status === 401 && !isCredentialPost) handleAuthError(error);
    throw error;
  }

  return json as T;
}

/**
 * Shorthand for GET with query params.
 *
 * ```ts
 * const data = await apiGet<{ stations: Station[] }>("/api/stations", { productionLineID: 100 });
 * ```
 */
export function apiGet<T = Record<string, unknown>>(
  url: string,
  params?: Record<string, string | number | boolean | null | undefined>,
  opts?: Omit<FetchJsonOpts, "method" | "body">,
): Promise<T> {
  let fullUrl = url;
  if (params) {
    const sp = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v != null && v !== "") sp.set(k, String(v));
    }
    const qs = sp.toString();
    if (qs) fullUrl += (url.includes("?") ? "&" : "?") + qs;
  }
  return apiJson<T>(fullUrl, { ...opts, method: "GET" });
}

/** POST with JSON body. */
export function apiPost<T = Record<string, unknown>>(
  url: string,
  body?: unknown,
  opts?: Omit<FetchJsonOpts, "method" | "body">,
): Promise<T> {
  return apiJson<T>(url, { ...opts, method: "POST", body });
}

/** PUT with JSON body. */
export function apiPut<T = Record<string, unknown>>(
  url: string,
  body?: unknown,
  opts?: Omit<FetchJsonOpts, "method" | "body">,
): Promise<T> {
  return apiJson<T>(url, { ...opts, method: "PUT", body });
}

/** PATCH with JSON body. */
export function apiPatch<T = Record<string, unknown>>(
  url: string,
  body?: unknown,
  opts?: Omit<FetchJsonOpts, "method" | "body">,
): Promise<T> {
  return apiJson<T>(url, { ...opts, method: "PATCH", body });
}

/** DELETE (body optional). */
export function apiDelete<T = Record<string, unknown>>(
  url: string,
  opts?: FetchJsonOpts,
): Promise<T> {
  return apiJson<T>(url, { ...opts, method: "DELETE" });
}
